import type { TreasuryBillDetail, TreasuryBillTransaction } from '@typeDefs/domain/assets';
import { accretedValue } from '@utils/treasuryBillAccretion';

/**
 * S5 — value-history series (#219). Pure data-shaper for the T-Bill detail chart.
 *
 * Model: the value line is `r(t) × Vfull(t)`, where `Vfull(t)` is the straight-line pull-to-par
 * value of the ORIGINAL position (`pricePaid` → `originalFaceValue`) and `r(t)` is the remaining
 * face fraction (starts at 1, steps down at each completed sale). The line only bends at a sale (a
 * vertical drop); there is NO kink at `now` — historical (solid) and projected (dashed) share one
 * gradient. See value-history-chart-design.md. A true per-day valuation series is a client-api
 * follow-on (preservoir-ai#87); this util calls no API.
 */

export type ValueSegmentKind = 'held' | 'projected' | 'pending';
export type ValueEventKind = 'purchase' | 'saleRequested' | 'saleComplete' | 'maturity' | 'now';

export interface ValueHistoryPoint {
  date: Date;
  value: number;
  segment: ValueSegmentKind;
  isCostPoint?: boolean;
  eventKind?: ValueEventKind;
  label?: string;
}

export interface ValueHistorySeries {
  points: ValueHistoryPoint[];
  placeholder: 'pending-acceptance' | 'rejected' | null;
  terminal: boolean;
  hasPendingSegment: boolean;
  pendingSegment: ValueHistoryPoint[];
  yDomain: { min: number; max: number };
}

/** Interim: the stale web-client union exposes completed sales as `'sale'`. Widens to
 *  Sale/StakeSale when preservoir-ai#87 corrects the contract — change here only. */
function isCompletedSale(txn: TreasuryBillTransaction): boolean {
  return txn.transactionType === 'sale';
}

/** Straight-line pull-to-par value of the ORIGINAL full position at time `at`. */
function fullHoldingValue(detail: TreasuryBillDetail, at: number): number {
  return accretedValue({
    purchasePrice: detail.pricePaid,
    faceValue: detail.originalFaceValue || detail.faceValue,
    acceptanceDate: detail.orderDateTimeUtc,
    maturityDate: detail.maturityDate,
    asOf: new Date(at),
  });
}

function placeholderSeries(kind: 'pending-acceptance' | 'rejected'): ValueHistorySeries {
  return {
    points: [],
    placeholder: kind,
    terminal: false,
    hasPendingSegment: false,
    pendingSegment: [],
    yDomain: { min: 0, max: 0 },
  };
}

export function buildValueHistorySeries(
  detail: TreasuryBillDetail,
  asOf: Date = new Date(),
): ValueHistorySeries {
  // ── Lifecycle A / I — no value curve. ──
  if (detail.status === 'pending') return placeholderSeries('pending-acceptance');
  if (detail.status === 'rejected') return placeholderSeries('rejected');

  const purchaseTime = detail.orderDateTimeUtc.getTime();
  const nowTime = asOf.getTime();
  const maturityTime = detail.maturityDate.getTime();
  const originalFace = detail.originalFaceValue || detail.faceValue;
  const matured = detail.status === 'matured' || nowTime >= maturityTime;

  const vFull = (at: number) => fullHoldingValue(detail, at);

  const points: ValueHistoryPoint[] = [];
  const plotted: number[] = [];
  const record = (v: number): number => {
    plotted.push(v);
    return v;
  };

  // ── Purchase anchor — first point, never (0,0). ──
  points.push({
    date: new Date(purchaseTime),
    value: record(detail.pricePaid),
    segment: 'held',
    isCostPoint: true,
    eventKind: 'purchase',
    label: 'Purchased bill',
  });

  // ── Completed sales, chronological: each is a vertical drop (two coincident points). ──
  const sales = detail.transactions
    .filter(isCompletedSale)
    .slice()
    .sort(
      (a, b) =>
        new Date(a.transactionDateTime).getTime() - new Date(b.transactionDateTime).getTime(),
    );

  let r = 1;
  let soldOut = false;
  for (const sale of sales) {
    const t = new Date(sale.transactionDateTime).getTime();
    const clamped = Math.min(Math.max(t, purchaseTime), maturityTime);
    const seg: ValueSegmentKind = clamped <= nowTime ? 'held' : 'projected';

    const preValue = r * vFull(clamped);
    const soldFace = Math.max(0, sale.amount);
    const rAfter = Math.max(0, r - soldFace / originalFace);
    const postValue = rAfter * vFull(clamped);

    points.push({
      date: new Date(clamped),
      value: record(preValue),
      segment: seg,
      isCostPoint: true,
      eventKind: 'saleRequested',
      label: 'Sale requested',
    });
    points.push({
      date: new Date(clamped),
      value: record(postValue),
      segment: seg,
      isCostPoint: true,
      eventKind: 'saleComplete',
      label: 'Sale complete',
    });

    r = rAfter;
    if (r <= 0) {
      soldOut = true;
      break;
    }
  }

  // ── Now boundary + maturity endpoint — unless the holding is closed. ──
  if (!soldOut) {
    const reducedMaturity = r * originalFace; // == current faceValue at the final fraction
    if (matured) {
      // Terminal — single held line to the reduced face value. No projected leg.
      points.push({
        date: new Date(maturityTime),
        value: record(reducedMaturity),
        segment: 'held',
        eventKind: 'maturity',
        label: 'Matured',
      });
    } else {
      const nowValue = r * vFull(nowTime);
      // The `now` boundary carries BOTH held and projected so the legs join with no kink.
      points.push({
        date: new Date(nowTime),
        value: record(nowValue),
        segment: 'held',
        eventKind: 'now',
        label: 'Now',
      });
      points.push({
        date: new Date(nowTime),
        value: nowValue,
        segment: 'projected',
        eventKind: 'now',
        label: 'Now',
      });
      points.push({
        date: new Date(maturityTime),
        value: record(reducedMaturity),
        segment: 'projected',
        eventKind: 'maturity',
        label: 'Maturity',
      });
    }
  }

  points.sort((a, b) => a.date.getTime() - b.date.getTime());

  // ── Pending (requested-but-not-completed) sale overlay — C / F. ──
  const pendingSaleAmount = detail.pendingSaleAmount ?? 0;
  const hasPendingSegment = !soldOut && !matured && pendingSaleAmount > 0;
  let pendingSegment: ValueHistoryPoint[] = [];
  if (hasPendingSegment) {
    const nowValue = r * vFull(nowTime);
    const rPending = Math.max(0, r - pendingSaleAmount / originalFace);
    const postPendingNow = rPending * vFull(nowTime);
    const pendingMaturity = rPending * originalFace;
    pendingSegment = [
      {
        date: new Date(nowTime),
        value: nowValue,
        segment: 'pending',
        eventKind: 'saleRequested',
        label: 'Pending sale',
      },
      { date: new Date(nowTime), value: postPendingNow, segment: 'pending', label: 'Pending sale' },
      {
        date: new Date(maturityTime),
        value: pendingMaturity,
        segment: 'pending',
        label: 'Pending sale',
      },
    ];
    plotted.push(nowValue, postPendingNow, pendingMaturity);
  }

  // ── Fixed frame: top = original maturity value; bottom fits the lowest plotted value. ──
  const lowest = plotted.length ? Math.min(...plotted) : detail.pricePaid;
  const yDomain = { min: Math.min(detail.pricePaid, lowest), max: originalFace };

  return {
    points,
    placeholder: null,
    terminal: matured || soldOut,
    hasPendingSegment,
    pendingSegment,
    yDomain,
  };
}
